import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Input, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useDeleteProtocolMutation } from "../../../redux/protocols/protocolAPI";
import { updateOpenModalDelete } from "../../../redux/protocols/protocolsSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { styleDeleteButton } from "../../../utils/utils";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function ProtocolDeleteModal() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [confirmName, setConfirmName] = useState("");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { protocol, openModalDelete } = useSelector((state: RootState) => state.protocol);
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [launchDeleteProtocol, { isLoading, isSuccess, error, isError, reset: resetDeleteProtocol }] =
		useDeleteProtocolMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const disableButton = () => {
		if (confirmName && protocol && protocol.name && confirmName.toLowerCase() === protocol.name.toLowerCase()) {
			return false;
		}
		return true;
	};

	const onClickClose = () => {
		dispatch(updateOpenModalDelete(false));
		setConfirmName("");
		resetDeleteProtocol();
	};

	const handleDeleteItem = async () => {
		const token = await GETJwtToken();
		launchDeleteProtocol({ id: protocol.id, token });
	};

	const handleSubmitDeleteItem = async (event: any) => {
		if (event.key === "Enter" && !disableButton()) {
			const token = await GETJwtToken();
			launchDeleteProtocol({ id: protocol.id, token });
		}
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (isSuccess || isError) {
			onClickClose();
		}
	}, [isSuccess, isError]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Modal onOk={onClickClose} closable={false} onCancel={onClickClose} open={openModalDelete} footer={null}>
			<Spin spinning={isLoading}>
				<div>
					<div className='text-center'>
						<FontAwesomeIcon
							icon={faTriangleExclamation}
							className='generalStyles__warning generalStyles__deleteItemIcon'
						/>
						<p className='mt-3 drawer__text'>
							{t("organizationManagement.deleteDescription.text1")}
							<b className='generalStyles__warning'>{t("organizationManagement.deleteDescription.text2")} </b>
							{t("general.theProtocol")}{" "}
							<b className='generalStyles__warning'>{protocol && protocol.name ? protocol.name : ""} </b>
							{t("general.confirmName")}{" "}
						</p>
						<Input
							placeholder={t("general.name")}
							onChange={(e) => {
								setConfirmName(e.target.value);
							}}
							value={confirmName}
							onKeyDown={handleSubmitDeleteItem}
							status='warning'
						/>
					</div>
					<div className='generalStyles__flexEnd mt-4'>
						<div>
							<Button
								htmlType='submit'
								className={styleDeleteButton(disableButton(), theme)}
								onClick={handleDeleteItem}
								disabled={disableButton()}
							>
								{t("organizationManagement.delete")}
							</Button>
							<Button onClick={onClickClose} className='buttonStyle__4'>
								{t("organizationManagement.close")}
							</Button>
						</div>
					</div>
				</div>
			</Spin>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2 isError={isError} isSuccess={isSuccess} requestType='DELETE' error={error} name='Protocol' />
		</Modal>
	);
}

export default ProtocolDeleteModal;
